import find from 'lodash/find';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import { connect } from '~/redux/connect';
import cn from 'classnames';

import {QrScanner} from '~/ui/QrScanner/QrScanner';
import MessageList from "~/ui/components/MessageList/MessageList";
import {
    getAllResultParams,
    getCustomActionResult,
    getGlobalActions,
    getGroups,
    getModelName,
    getModelTitle,
    getModelTooltip,
    getResultsValue,
    getSelectThroughout,
    getSelectedRows,
    getPageAction,
} from "~/stateGetter";
import {presentActionInfo} from "~/statePresenter";
import {performCustomAction, performCustomScanQrAction} from "~/rx/model/customAction";
import stripTags from 'striptags';
import ItemGrid from '~/ui/ItemGrid';
import identify from "~/identify";
import css from "./css.css";
import ElementTooltipContainer from "~/ui/Tooltip/ElementTooltipContainer";
import groupBy from 'lodash/groupBy';
import omit from 'lodash/omit';
import memoize from "lodash/memoize";

import { SpinnerButton as Button, Dialog, Divider, HStack, IconMenu, MenuItem, Text, Tooltip, VStack } from "~/native-ui";
import { ConfirmField } from './ConfirmField';

const ActionItem = (action) => {
    let tooltip = action.tooltip;

    let captionItem =  tooltip && tooltip ?
        <ElementTooltipContainer leftMargin tooltip={tooltip}>{action.caption}</ElementTooltipContainer> :
        action.caption;

    return (<MenuItem
        icon={action.icon}
        iconFamily={action.iconFamily}
        iconColor={action.iconColor}
        caption={captionItem}
        value={action.value}
        onClick={action.onClick}
        disabled={!action.allowDirty && action.disabled}
        mx={action.mx}
    />)
};


const ActionButton = (props) => {
    const icon = props.icon,
          iconFamily = props.iconFamily,
          caption = props.caption,
          allowDirty = props.allowDirty,
          label = props.showLabel || !icon ? caption : '',
          button = (
                <Button
                    primary
                    iconSize="sm"
                    key={props.value}
                    icon={icon}
                    iconFamily={iconFamily}
                    label={label}
                    onPress={props.onClick}
                    disabled={!allowDirty && props.disabled}
                />
            )

    if (!icon || props.showLabel) {
        return button;
    }
    
    return (
        <Tooltip tooltip={caption} disabled={props.disabled} >{ button }</Tooltip>
    )
};

/**
 * @typedef ActionsObjectGroup
 * @type {object}
 * 
 * @property {string} icon
 * @property {string} [iconFamily]
 * @property {string} name
 * @property {number} order
 * @property {string} title
 */

/**
 * @typedef ActionsObject
 * @type {object}
 * @property {function} action
 * @property {string} groupName
 * @property {ActionsObjectGroup} group
 * @property {boolean} isActive
 * @property {boolean} [requiresSelection]
 * @property {number} [criticalConfirmQuantity]
 * @property {string} [criticalConfirmText]
 * @property {boolean} disabled
 * 
 * @property {string} caption
 * @property {string} [captionActive]
 * @property {string} [dialogTitle]
 * @property {string} [grid]
 * @property {boolean} hasPermission
 * @property {string} [icon]
 * @property {string} [iconFamily]
 * @property {boolean} [noReload]
 * @property {boolean} [scanQr]
 * @property {boolean} [tooltip]
 * @property {string} value
 */



/**
 * Action Menu Component
 *
 * @type {React.ComponentClass<{ actions: ActionsObject[]}>} 
 * @returns {React.ReactElement} ActionsMenu
 */
export class ActionMenu extends Component {
    static propTypes = {
        actions: PropTypes.array.isRequired,
        activeAction: PropTypes.object,
        isActionActive: PropTypes.bool.isRequired,
        title: PropTypes.string,
        otherItems: PropTypes.array.isRequired,
        disabled: PropTypes.bool.isRequired,
        dropdownMode: PropTypes.bool,
    };

    static defaultProps = {
        isActionActive: false,
        otherItems: [],
        disabled: false
    };

    state  = {
        confirmAction: null,
        showDialog: false,
        criticalConfirmValid: false,
    };

    get disablePrimaryConfirm () {
        return !this.state.criticalConfirmValid;
    }

    componentDidMount() {
        this.autoOpenModel()
    }

    componentDidUpdate(prevProps) {
        if (this.props.modelAction !== prevProps.modelAction) {
            this.autoOpenModel()
        }
    }

    autoOpenModel = () => {
        const { modelAction, actions } = this.props;
        if (modelAction?.action) {
            const action = find(actions, {value: modelAction.action})
            this.performAction(action);
        }
    }

    hideConfirmActionDialog = () => {
        this.props.onCloseAction?.();
        this.setState({showDialog: false});
    };

    setActionDialog = (actionName, criticalConfirm) => {
        this.setState({ confirmAction: actionName, showDialog: true, criticalConfirmValid: !criticalConfirm });
    };


    performAction = async (action) => {
        if (!action) {
            return;
        }

        const shouldConfirmAction = action.confirmation === true
            || action.requiresSelection && action.confirmation <= this.props.selectedRowCount
            || action.criticalConfirmText
            || action.grid
            || action.scanQr
            || this.props.modelAction?.action === action.value;

        if (shouldConfirmAction) {
            return this.setActionDialog(action.value);
        }

        action.action(action.value);
    }

    onChangeCriticalConfirm = (isMatch) => {
        this.setState({ criticalConfirmValid: isMatch });
    }

    prepareAction = (theActions, action) => {
        if(action.disabled) { return theActions; }
        

        theActions.push({
            ...action,
            onClick: async () => this.performAction(action)
        });

        return theActions;
    };
    
    renderDialog = () => {
        let actionName = this.state.confirmAction;
        if(!actionName || !actionName.length) {
            return;
        }
            
        let action = find(this.props.actions, {value: actionName});
        
        if(action.scanQr) {
            return this.renderQrCodeDialog(action);
        }

        if(action.grid) {
            return this.renderActionGrid(action);
        }

        if(action.requiresSelection) {
            return this.renderActionRequiresSelectionDialog(action);    
        }
        
        return this.renderActionConfirmDialog(action);
    };

    renderBaseDialog = (children, { translate = {}, showConfirmActions = true, showCancel = true, showConfirm = true, dialogProps = {} } = {}) => {
        let { trans, title } = this.props;

        let confirmedActionName = this.state.confirmAction,
            showDialog = this.state.showDialog,
            confirmedAction = find(this.props.actions, {value: confirmedActionName}),
            actionTitle = get(confirmedAction, 'caption');

        let transData = {
            actionTitle,
            itemTitle: stripTags(title),
        };

        let confirmTitle = confirmedAction.dialogTitle ?
            confirmedAction.dialogTitle :
            trans('confirmAction.title', transData);

        const confirmActions =  !showConfirmActions ? null : [
            {
                label: translate.okay ?? actionTitle,
                disabled: this.disablePrimaryConfirm,
                onClick: () => {
                    confirmedAction.action(confirmedActionName);
                    this.hideConfirmActionDialog();
                }
            },
            !showCancel ? null : {
                label: translate.no ?? trans('no'),
                onClick: this.hideConfirmActionDialog
            }
        ].filter(Boolean);

        return (
            <Dialog
                className={css.dialog}
                actions={confirmActions}
                active={showDialog}
                title={confirmTitle}
                onOverlayClick={this.hideConfirmActionDialog}
                onEscKeyDown={this.hideConfirmActionDialog}
                {...dialogProps}
            >
                {children}
            </Dialog>
        )
    };

    renderQrCodeDialog(confirmedAction) {
        const scannerProps = {
            modelId: this.props.modelId,
            onScan: async (qr_code_string) => {
                if (await confirmedAction.action(this.state.confirmAction, { qr_code_string })) {
                    this.hideConfirmActionDialog()
                }
            }
        }

        return this.renderBaseDialog(<QrScanner { ...scannerProps} />, { showConfirm: false })
    }

    renderActionGrid = (confirmedAction) => {
        let requiresSelectionMessage = null,
            requiresSelection = confirmedAction.requiresSelection,
            { trans, rowCount, modelTitle } = this.props;

        if (requiresSelection) {
            const transData = {
                count: rowCount,
                modelNamePlural: modelTitle
            }

            const messagesListProps = {
                messages: [
                    {message: trans('requiresSelection.message', transData), type: "warning"}
                ],
                level: 0,

            }

            requiresSelectionMessage = <MessageList { ...messagesListProps} />
        }

        return this.renderBaseDialog(
            <React.Fragment>
                { requiresSelectionMessage }
                <ItemGrid
                    modelId={this.props.modelId}
                    gridName={confirmedAction.grid}
                    view={identify.VIEW_EDIT}
                    className={css.itemGrid}
                />
            </React.Fragment>,
            {
                dialogProps: {
                    type: "medium-auto"
                }
            }
        );
    };

    renderConfirmField (action) {
        const { criticalConfirmText, criticalConfirmQuantity = 0 } = action;
        if (!criticalConfirmText) {
            return null;
        }

        return (
            <ConfirmField
                modelId={this.props.modelId}
                threshold={criticalConfirmQuantity || 0}
                onChange={this.onChangeCriticalConfirm}
                valueToConfirm={action.criticalConfirmText}
            />
        )
    }

    renderActionRequiresSelectionDialog = (action) => {
        let { trans, title, rowCount, modelTitle } = this.props;

        const actionProps = {
            count: rowCount,
            modelNamePlural: modelTitle
        }

        let transData = {
                actionTitle: trans('requiresSelection.title', actionProps),
                itemTitle: stripTags(title),
                ...actionProps,
            },
            confirmMessage = trans('requiresSelection.message', transData)

        return this.renderBaseDialog((
            <VStack space="2">
                <Text>{confirmMessage}</Text>
                {this.renderConfirmField(action)}
            </VStack>
        ), {
            translate: {
                okay: transData.actionTitle
            }
        })
    };



    renderActionConfirmDialog = (action) => {
        let { trans, title } = this.props;

        let confirmedActionName = this.state.confirmAction,
            confirmedAction = find(this.props.actions, {value: confirmedActionName}),
            actionTitle = get(confirmedAction, 'caption');

        let transData = {
            actionTitle,
            itemTitle: stripTags(title)
        };

        let confirmMessage = trans(get(confirmedAction, 'confirmationMessage', 'confirmAction.message'), transData);

        return this.renderBaseDialog((
            <VStack space="2" className="test">
                <Text>{confirmMessage}</Text>
                {this.renderConfirmField(action)}
            </VStack>
        ))
    };


    getButtonProps = memoize((disabled) => ({ disabled: disabled, primary: true, iconSize: "sm" }))

    renderMenuGroup = ([group, groupActions]) => {
        let { activeGroup, activeAction, isActionActive, className, menuClassName, disabledClassName, } = this.props,
            iconClassName = cn(menuClassName, {[disabledClassName]: isActionActive}),
            isActiveGroup = !!isActionActive && activeGroup?.name === group,
            icon = activeAction && isActiveGroup ? 'spinning' : get(groupActions, "0.group.icon", 'more-vert'),
            iconFamily = !activeAction ? get(groupActions, "0.group.iconFamily") : undefined ;
            
            if (groupActions.length <= 0) {
            return null
        }

        if (groupActions.length === 1) {
            let action = groupActions[0];
            action = {
                ...action,
                icon,
                iconFamily,
                disabled: action.disabled || this.props.disabled,
                key: action.value,
                fontSize: "xs"
            };

            return {
                actions: [action]
            }
        }



        return {
            "aria-description":get(groupActions, "0.group.title", group),
            tooltip:get(groupActions, "0.group.title", group),
            key:get(groupActions, "0.group.title", group),
            icon: icon,
            iconFamily: iconFamily,
            iconClassName:iconClassName,
            buttonProps:this.getButtonProps(this.props.disabled),
            actions: groupActions
        }

    }


    renderMenu () {
        let { actions, activeAction, otherItems, trans } = this.props;

        actions = actions.reduce(this.prepareAction, []).sort((a, b) => a.order - b.order);

        const label = activeAction ? activeAction.caption : trans('actions');

        let groupedActions = groupBy(actions, "groupName");
        const flatActions = get(groupedActions, "", []).map((props) => ({
            ...props,
            fontSize: "xs",
            disabled: props.disabled || this.props.disabled,
            key:props.value,
        }));

        const defaultGroup = get(groupedActions, "undefined", []);

        groupedActions = omit(groupedActions, "undefined", "");

        const actionElements = Object.entries(groupedActions).map(this.renderMenuGroup)

        const actionButtonsCount = flatActions.length + actionElements.length + (otherItems?.length ?? 0);

        if (actionButtonsCount.length < 1) {
            return null;
        }


        const MenuComponent = this.props.dropdownMode ? DropdownMenu : FlatMenu;
        return  <MenuComponent
            actionElements={actionElements}
            flatActions={flatActions}
            otherItems={otherItems}
            defaultGroups={[this.renderMenuGroup([undefined, defaultGroup])]}
            buttonProps={this.getButtonProps(this.props.disabled)}
        />;
    }

    render() {
        let { actions, className, } = this.props;
        
        return (
            <React.Fragment>
                {this.renderDialog()}
                <React.Fragment>{actions.length ? this.renderMenu() : null}</React.Fragment>
            </React.Fragment>
        );
    }
}


/**
 * @typedef MenuGroups
 * @type {object}
 * 
 * @property {ActionsObject[]} defaultGroups.actions
 */



/**
 * @typedef MenuProps
 * @type {object}
 * 
 * @property {MenuGroups[]} [actionElements]
 * @property {Array} flatActions
 * @property {Array} otherItems
 * @property {(MenuGroups | null)[]} [defaultGroups]
 * @property {boolean} [noDivider]
 */


const renderAction  = (actionProps) => <ActionItem {...actionProps} key={actionProps.value ?? actionProps.value ?? index + "$"} />


/**
 * Dropdown Action Menu Component
 * 
 *
 * @type {React.FC<MenuProps>} 
 * @returns {React.ReactElement} ActionsMenu
 */
const DropdownMenu = (props) => {
    /**
     * 
     * @param {(MenuGroups | null)[]} actions 
     * @returns 
     */
    const getFlatActions = (actions) => actions
        .reduce((flattenedActions, itemProps, index) => !itemProps
            ? flattenedActions
            : flattenedActions.concat(
                itemProps.actions.map((actionProps) => renderAction({...actionProps, iconColor: "primary.500", mx: "2"  })),
                index === actions.length - 1 ? null : <Divider key={index} orientation="horizontal" bg="coolGray.300" h="1px" mx="8px" width="calc(100% - 16px)" />
            )
        , [])

    const allActions = getFlatActions(props.actionElements)
        .concat(props.flatActions.map((actionProps) => renderAction({...actionProps, iconColor: "primary.500", mx: "2"  }))
        .concat(props.otherItems)
        .concat(getFlatActions(props.defaultGroups)));

    if (allActions.length === 1) return <>{allActions}</>

    return (
        <IconMenu
            aria-description={"Actions Menu"}
            tooltip={"Actions Menu"}
            icon={"more-vert"}
            buttonProps={props.buttonProps}
            // buttonProps={this.getButtonProps(this.props.disabled)}
        >
            {allActions}
        </IconMenu>
    )
}



/**
 * Flat Action Menu Component
 * 
 *
 * @type {React.FC<MenuProps>} 
 * @returns {React.ReactElement} ActionsMenu
 */
const FlatMenu = (props) => {

    /**
     * 
     * @param {(MenuGroups | null)[]} actions 
     * @returns 
     */
    const getFlatActions = (actions) => actions
        .reduce((flattenedActions, itemProps, index) => {
            if (!itemProps) return flattenedActions;
            
            const { actions, ...menuProps } = itemProps;

            if (itemProps.actions.length === 1)
                return flattenedActions.concat(<ActionButton {...itemProps.actions[0]} />);
            return flattenedActions.concat(
                <IconMenu  {...menuProps}>
                    {actions.map((actionProps) => renderAction(actionProps))}
                </IconMenu>
            )
        }
        , [])

    const allActions = getFlatActions(props.actionElements, "prop")
        .concat(props.flatActions.map(actionProps => <ActionButton {...actionProps} />))
        .concat(props.otherItems)
        .concat(getFlatActions(props.defaultGroups)).map((el, index) => {
            const node = React.cloneElement(el, { key: !el.key ? index + "$" : el.key});
            return node;
        })

    return (
        <React.Fragment>
            <React.Fragment>{ !props.noDivider && <Divider orientation="vertical" bg="coolGray.300" h="5" mx="2" />}</React.Fragment>
            <HStack space={2} alignItems="center">
            {allActions}
            </HStack>
        </React.Fragment>
    )
}


/**
 * Action Menu Component
 *
 * @type {React.ComponentClass<{ modelId: string, actions?: ActionsObject[], dropdownMode?: boolean }>} 
 * @returns {React.ReactElement} ActionsMenu
 */
export const TableActionMenu = connect((state, {modelId}) => {
    return {
        modelAction: getPageAction(state, modelId),
        modelName: getModelName(state, modelId),
        modelTitle: getModelTitle(state, modelId),
        actions: getGlobalActions(state, modelId),
        rowCount: getResultsValue(state, modelId).total,
        customActionResult: getCustomActionResult(state, modelId),
        tooltip:  getModelTooltip(state, modelId, 'global_actions'),
        selectedThroughout: getSelectThroughout(state, modelId),
        selectedRows: getSelectedRows(state, modelId),
        groups: getGroups(state, modelId),
        ...getAllResultParams(state, modelId),
    }
}, (dispatch, {modelId}) => {
    return {
        executeAction: (actionName) => dispatch(performCustomAction(modelId, actionName)),
        executeScanQrAction: (actionName, data) => dispatch(performCustomScanQrAction(modelId, actionName, data)),
    }
}, ({groups, actions, customActionResult, tooltip, selectedThroughout, selectedRows, rowCount, modelTitle, modelAction}, {executeAction, executeScanQrAction}, {...otherOwnProps}) => {
    const actionInfo = presentActionInfo({
        groups, actions, executeAction, executeScanQrAction, actionResult: customActionResult,
        selectedRows: !selectedThroughout ? selectedRows : [true]
    });
    return {
        tooltip,
        rowCount,
        modelTitle,
        modelAction,
        selectedRowCount: selectedRows.length,
        ...otherOwnProps,
        ...actionInfo,
        actions: [...(otherOwnProps.actions ?? []), ...actionInfo.actions ]
    }
})(ActionMenu);